<template>
  <div>
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <template v-slot:activator="{ on, attrs }">
        <v-btn 
          color="primary" 
          text 
          v-bind="attrs" 
          v-on="on"
          class="anonymous-upgrade-btn"
        >
          <v-icon left>mdi-account-convert</v-icon>
          Create Account
        </v-btn>
      </template>
      
      <v-card>
        <v-card-title class="headline">
          Save Your Preferences
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <p class="mb-4">Create an account to save your preferences and access them from any device.</p>
          
          <v-alert v-if="error" type="error" dense class="mb-4">{{ error }}</v-alert>
          
          <v-tabs v-model="activeTab" centered>
            <v-tab key="email">Email</v-tab>
            <v-tab key="google">Google</v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="activeTab" class="mt-4">
            <!-- Email Tab -->
            <v-tab-item key="email">
              <v-form @submit.prevent="handleEmailUpgrade" ref="emailForm">
                <v-text-field
                  v-model="email"
                  label="Email"
                  type="email"
                  required
                  outlined
                  dense
                  :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Email must be valid']"
                  :disabled="loading"
                ></v-text-field>
                
                <v-text-field
                  v-model="password"
                  label="Password"
                  type="password"
                  required
                  outlined
                  dense
                  :rules="[v => !!v || 'Password is required', v => v.length >= 6 || 'Password must be at least 6 characters']"
                  :disabled="loading"
                ></v-text-field>
                
                <v-text-field
                  v-model="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  required
                  outlined
                  dense
                  :rules="[v => !!v || 'Please confirm your password', v => v === password || 'Passwords must match']"
                  :disabled="loading"
                ></v-text-field>
                
                <v-btn 
                  block 
                  color="primary" 
                  type="submit" 
                  :loading="loading" 
                  :disabled="loading || !isEmailFormValid"
                  class="mt-4"
                >
                  Create Account
                </v-btn>
              </v-form>
            </v-tab-item>
            
            <!-- Google Tab -->
            <v-tab-item key="google">
              <div class="text-center py-6">
                <p class="mb-4">Link your account with Google to save your preferences.</p>
                <v-btn 
                  color="primary" 
                  outlined 
                  @click="handleGoogleUpgrade" 
                  :loading="loading" 
                  :disabled="loading"
                  class="google-btn"
                >
                  <img src="@/assets/google-icon.svg" alt="Google" class="google-icon mr-2">
                  Continue with Google
                </v-btn>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        
        <v-card-actions class="pb-4 px-4">
          <v-btn text @click="dialog = false" :disabled="loading">Cancel</v-btn>
          <v-spacer></v-spacer>
          <div class="caption text--secondary">
            Your preferences will be saved to your new account.
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'AnonymousUpgrade',
  data() {
    return {
      dialog: false,
      activeTab: 0,
      email: '',
      password: '',
      confirmPassword: '',
      error: null,
      loading: false
    };
  },
  computed: {
    isAnonymous() {
      return this.$store.getters['auth/isAnonymous'];
    },
    isEmailFormValid() {
      return this.email && 
             this.password && 
             this.password === this.confirmPassword && 
             this.password.length >= 6 &&
             /.+@.+\..+/.test(this.email);
    }
  },
  methods: {
    async handleEmailUpgrade() {
      if (!this.isEmailFormValid) {
        return;
      }
      
      this.error = null;
      this.loading = true;
      
      try {
        const result = await this.$store.dispatch('auth/linkAnonymousWithEmail', {
          email: this.email,
          password: this.password
        });
        
        if (result.success) {
          this.dialog = false;
          this.$emit('upgrade-success');
        } else if (result.error) {
          this.error = result.error;
        }
      } catch (err) {
        console.error('Error upgrading anonymous account with email:', err);
        this.error = err.message || 'Failed to create account. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    async handleGoogleUpgrade() {
      this.error = null;
      this.loading = true;
      
      try {
        // For Google upgrade, we'll just sign in with Google which will automatically link
        // if the user was previously anonymous
        await this.$store.dispatch('auth/loginWithGoogle');
        this.dialog = false;
        this.$emit('upgrade-success');
      } catch (err) {
        console.error('Error upgrading anonymous account with Google:', err);
        this.error = err.message || 'Failed to link with Google. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.google-icon {
  width: 18px;
  height: 18px;
}

.google-btn {
  background-color: white;
}

.anonymous-upgrade-btn {
  text-transform: none;
}

/* Dark mode adjustments */
.theme--dark .google-btn {
  background-color: #1E1E1E;
  border-color: #555;
}
</style> 